import type { JobCluster } from '~/shared/data-contract/job-cluster';
import type { JobFamily } from '~/shared/data-contract/job-family';

import type { DraftFamilyRow } from '../onboarding/table/rows';
import { isTemporaryDraftItem } from '../onboarding/table/rows';

/** Result-like type so any Result<T, E> is acceptable and can be tested with mocks. */
type ResultLike<T, E = unknown> = { readonly error?: E; readonly isErr: boolean; readonly value?: T };

/**
 * Fetcher interface for applying onboarding draft (delete/put/patch per element).
 * Injected so this module can be tested in isolation with mocks.
 */
export type ApplyOnboardingDraftFetchers = {
    readonly deleteJobClusters: (
        jobClusters: readonly [JobCluster, ...(readonly JobCluster[])],
    ) => Promise<ResultLike<unknown>>;
    readonly deleteJobFamilies: (
        jobFamilies: readonly [JobFamily, ...(readonly JobFamily[])],
    ) => Promise<ResultLike<unknown>>;
    readonly patchJobCluster: (args: {
        readonly jobCluster: JobCluster;
        readonly withAutoTranslation: boolean;
    }) => Promise<ResultLike<JobCluster>>;
    readonly patchJobFamily: (args: {
        readonly jobFamily: JobFamily;
        readonly languageCode: string;
        readonly withAutoTranslation: boolean;
    }) => Promise<ResultLike<JobFamily>>;
    readonly putJobCluster: (args: {
        readonly jobArchitectureId: string;
        readonly jobCluster: JobCluster;
        readonly jobFamilyId: string;
    }) => Promise<ResultLike<JobCluster>>;
    readonly putJobFamily: (args: {
        readonly jobArchitectureId: string;
        readonly jobFamily: JobFamily;
    }) => Promise<ResultLike<JobFamily>>;
};

export type PartitionedDraft = {
    readonly patchClusters: readonly JobCluster[];
    readonly patchFamilies: readonly JobFamily[];
    readonly putClusters: readonly { readonly jobCluster: JobCluster; readonly jobFamilyId: string }[];
    readonly putFamilies: readonly JobFamily[];
    readonly removedClusters: readonly JobCluster[];
    readonly removedFamilies: readonly JobFamily[];
};

type FamilyPartition = {
    readonly patchClusters: readonly JobCluster[];
    readonly patchFamilies: readonly JobFamily[];
    readonly putClusters: readonly { readonly jobCluster: JobCluster; readonly jobFamilyId: string }[];
    readonly putFamilies: readonly JobFamily[];
    readonly removedClusters: readonly JobCluster[];
    readonly removedFamilies: readonly JobFamily[];
};

function partitionFamilyClusters(
    familyId: string,
    subRows: DraftFamilyRow['subRows'],
): Pick<FamilyPartition, 'patchClusters' | 'putClusters' | 'removedClusters'> {
    const removedClusters: JobCluster[] = [];
    const putClusters: { jobCluster: JobCluster; jobFamilyId: string }[] = [];
    const patchClusters: JobCluster[] = [];

    for (const cluster of subRows ?? []) {
        if (cluster.status === 'removed') {
            removedClusters.push(cluster.data);
        } else if (isTemporaryDraftItem(cluster.status)) {
            putClusters.push({ jobCluster: cluster.data, jobFamilyId: familyId });
        } else if (cluster.status === 'updated') {
            patchClusters.push(cluster.data);
        }
    }

    return { patchClusters, putClusters, removedClusters };
}

function partitionOneFamily(family: DraftFamilyRow): FamilyPartition {
    const removedFamilies = family.status === 'removed' ? [family.data] : [];
    const putFamilies = isTemporaryDraftItem(family.status) ? [family.data] : [];
    const patchFamilies = family.status === 'updated' ? [family.data] : [];
    const clusters = partitionFamilyClusters(family.id, family.subRows);

    return {
        ...clusters,
        patchFamilies,
        putFamilies,
        removedFamilies,
    };
}

export function partitionDraft(draft: readonly DraftFamilyRow[]): PartitionedDraft {
    const patchClusters: JobCluster[] = [];
    const patchFamilies: JobFamily[] = [];
    const putClusters: { jobCluster: JobCluster; jobFamilyId: string }[] = [];
    const putFamilies: JobFamily[] = [];
    const removedClusters: JobCluster[] = [];
    const removedFamilies: JobFamily[] = [];

    for (const family of draft) {
        const part = partitionOneFamily(family);
        removedFamilies.push(...part.removedFamilies);
        putFamilies.push(...part.putFamilies);
        patchFamilies.push(...part.patchFamilies);
        removedClusters.push(...part.removedClusters);
        putClusters.push(...part.putClusters);
        patchClusters.push(...part.patchClusters);
    }

    return {
        patchClusters,
        patchFamilies,
        putClusters,
        putFamilies,
        removedClusters,
        removedFamilies,
    } as PartitionedDraft;
}

async function runDeletes(
    removed: Pick<PartitionedDraft, 'removedClusters' | 'removedFamilies'>,
    fetchers: ApplyOnboardingDraftFetchers,
): Promise<void> {
    if (removed.removedClusters.length > 0) {
        const result = await fetchers.deleteJobClusters(removed.removedClusters as [JobCluster, ...JobCluster[]]);
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
    if (removed.removedFamilies.length > 0) {
        const result = await fetchers.deleteJobFamilies(removed.removedFamilies as [JobFamily, ...JobFamily[]]);
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
}

async function runPatches(
    languageCode: string,
    patch: Pick<PartitionedDraft, 'patchClusters' | 'patchFamilies'>,
    fetchers: ApplyOnboardingDraftFetchers,
): Promise<void> {
    for (const jobFamily of patch.patchFamilies) {
        const result = await fetchers.patchJobFamily({ jobFamily, languageCode, withAutoTranslation: false });
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
    for (const jobCluster of patch.patchClusters) {
        const result = await fetchers.patchJobCluster({ jobCluster, withAutoTranslation: false });
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
}

async function runPuts(
    jobArchitectureId: string,
    put: Pick<PartitionedDraft, 'putClusters' | 'putFamilies'>,
    fetchers: ApplyOnboardingDraftFetchers,
): Promise<void> {
    for (const jobFamily of put.putFamilies) {
        const result = await fetchers.putJobFamily({ jobArchitectureId, jobFamily });
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
    for (const { jobCluster, jobFamilyId } of put.putClusters) {
        const result = await fetchers.putJobCluster({ jobArchitectureId, jobCluster, jobFamilyId });
        if (result.isErr) {
            throw result.error instanceof Error ? result.error : new Error(String(result.error));
        }
    }
}

export type ApplyOnboardingDraftParams = {
    readonly draft: readonly DraftFamilyRow[];
    readonly jobArchitectureId: string;
    readonly languageCode: string;
};

/**
 * Applies the onboarding draft: delete removed, put new (added/accepted), patch existing (confirmed).
 * Uses the provided fetchers so the logic can be tested in isolation.
 */
export async function applyOnboardingDraft(
    params: ApplyOnboardingDraftParams,
    fetchers: ApplyOnboardingDraftFetchers,
): Promise<void> {
    const partitioned = partitionDraft(params.draft);
    await runDeletes(partitioned, fetchers);
    await runPuts(params.jobArchitectureId, partitioned, fetchers);
    await runPatches(params.languageCode, partitioned, fetchers);
}
